import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild } from '@angular/core';
import { ChatAttachment } from '../../models/chat.models';

/** Payload emitted when the user sends a message (text + optional attachments). */
export interface ChatInputPayload {
  text: string;
  attachments: ChatAttachment[];
}

@Component({
  selector: 'app-chat-input',
  templateUrl: './chat-input.component.html',
  styleUrls: ['./chat-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatInputComponent {
  @Input() busy = false;
  @Output() readonly send = new EventEmitter<ChatInputPayload>();
  @Output() readonly cancelRequest = new EventEmitter<void>();

  @ViewChild('ta') private readonly textarea?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('fileInput') private readonly fileInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('cameraInput') private readonly cameraInputRef?: ElementRef<HTMLInputElement>;

  readonly maxChars = 4000;
  value = '';
  recording = false;
  showExtras = false;
  private recognition: any = null;

  /** Queued attachments (images, PDFs, etc.) ready to send with the next message. */
  pendingAttachments: ChatAttachment[] = [];

  /** Max file size: 10 MB */
  private readonly maxFileSize = 10 * 1024 * 1024;

  /** Accepted file types for the file picker. */
  readonly acceptedFileTypes = [
    // Text / spreadsheets
    '.txt', '.csv', '.json', '.xml', '.md', '.log', '.xlsx', '.xls',
    // Images
    '.jpg', '.jpeg', '.png', '.webp', '.gif', '.bmp',
    // Documents
    '.pdf',
  ].join(',');

  /** Accepted types for camera capture. */
  readonly acceptedCameraTypes = 'image/jpeg,image/png,image/webp';

  constructor(private readonly cdr: ChangeDetectorRef) {}

  get remaining(): number { return this.maxChars - this.value.length; }
  /** Only render the counter when the user is within 500 chars of the limit — no visual noise otherwise */
  get showCounter(): boolean { return this.remaining < 500; }
  get canSend(): boolean { return !this.busy && (this.value.trim().length > 0 || this.pendingAttachments.length > 0); }

  /** Open/close the extras dropdown. Stops propagation so document click handler doesn't close it immediately. */
  toggleExtras(event: MouseEvent): void {
    event.stopPropagation();
    this.showExtras = !this.showExtras;
  }

  closeExtras(): void {
    this.showExtras = false;
  }

  /** Close extras when clicking anywhere outside the extras wrapper */
  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.showExtras) {
      this.showExtras = false;
      this.cdr.markForCheck();
    }
  }
  get micSupported(): boolean { return 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window; }
  get cameraSupported(): boolean { return 'mediaDevices' in navigator && 'getUserMedia' in navigator.mediaDevices; }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.submit();
    }
  }

  onInput(): void {
    const el = this.textarea?.nativeElement;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 180)}px`;
  }

  submit(): void {
    if (!this.canSend) return;
    const trimmed = this.value.trim();
    if (trimmed.length > this.maxChars) return;

    // Emit user's typed text + attachments separately.
    // File text content (CSV, Excel, TXT…) is NOT embedded in the visible message;
    // buildSendRequest() appends it to the API payload transparently.
    this.send.emit({ text: trimmed, attachments: [...this.pendingAttachments] });
    this.value = '';
    this.pendingAttachments = [];
    const el = this.textarea?.nativeElement;
    if (el) el.style.height = 'auto';
  }

  // ── Microphone (STT) ──────────────────────────────────────────

  toggleMic(): void {
    if (this.recording) {
      this.stopRecording();
    } else {
      this.startRecording();
    }
  }

  private startRecording(): void {
    const SpeechRecognitionClass = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognitionClass) return;
    this.recognition = new SpeechRecognitionClass();
    this.recognition.continuous = false;
    this.recognition.interimResults = true;
    this.recognition.lang = document.documentElement.lang || 'es-ES';
    this.recognition.onresult = (event: any) => {
      let transcript = '';
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      this.value = this.value ? this.value + ' ' + transcript : transcript;
      this.cdr.markForCheck();
    };
    this.recognition.onend = () => {
      this.recording = false;
      this.cdr.markForCheck();
    };
    this.recognition.onerror = () => {
      this.recording = false;
      this.cdr.markForCheck();
    };
    this.recognition.start();
    this.recording = true;
  }

  private stopRecording(): void {
    this.recognition?.stop();
    this.recording = false;
  }

  // ── File upload ────────────────────────────────────────────────

  triggerFileUpload(): void {
    this.fileInputRef?.nativeElement.click();
  }

  triggerCamera(): void {
    this.cameraInputRef?.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.processFile(file);
    input.value = '';
  }

  onCameraCapture(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.processFile(file);
    input.value = '';
  }

  removeAttachment(index: number): void {
    this.pendingAttachments.splice(index, 1);
    this.cdr.markForCheck();
  }

  private processFile(file: File): void {
    if (file.size > this.maxFileSize) {
      this.pendingAttachments.push({
        fileName: `⚠ ${file.name} (max 10 MB)`,
        mediaType: 'error',
        base64Data: '',
      });
      this.cdr.markForCheck();
      return;
    }

    if (this.isExcelFile(file)) {
      this.processExcelFile(file);
      return;
    }

    if (this.isTextFile(file)) {
      const reader = new FileReader();
      reader.onload = () => {
        const text = reader.result as string;
        const lineCount = text.split('\n').filter(l => l.trim().length > 0).length;
        this.pendingAttachments.push({
          fileName: file.name,
          mediaType: 'text/plain',
          base64Data: '',
          textContent: `[Archivo adjunto: ${file.name}]\n${text}`,
        });
        this.cdr.markForCheck();
      };
      reader.readAsText(file);
      return;
    }

    // Images & documents: read as base64 and add as attachment
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const base64 = dataUrl.split(',')[1] ?? '';
      this.pendingAttachments.push({
        fileName: file.name,
        mediaType: file.type || 'application/octet-stream',
        base64Data: base64,
      });
      this.cdr.markForCheck();
    };
    reader.readAsDataURL(file);
  }

  private isExcelFile(file: File): boolean {
    return file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
  }

  private isTextFile(file: File): boolean {
    return file.type.startsWith('text/')
      || file.name.endsWith('.csv')
      || file.name.endsWith('.json')
      || file.name.endsWith('.xml')
      || file.name.endsWith('.md')
      || file.name.endsWith('.log');
  }

  /** Parses an Excel file with SheetJS (lazy-loaded), converts the first sheet to CSV,
   *  and embeds the result in the message textarea — same flow as a CSV file upload. */
  private processExcelFile(file: File): void {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = e.target?.result as ArrayBuffer;
        const XLSX = await import('xlsx');
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const csv = XLSX.utils.sheet_to_csv(worksheet, { blankrows: false });
        const rowCount = csv.split('\n').filter(r => r.trim().length > 0).length - 1;
        this.pendingAttachments.push({
          fileName: `${file.name} (${rowCount} filas)`,
          mediaType: 'text/csv',
          base64Data: '',
          textContent: `[Importación Excel: ${file.name} — ${rowCount} filas]\n${csv}`,
        });
        this.cdr.markForCheck();
      } catch (err) {
        console.error('Excel parse error', err);
        this.pendingAttachments.push({
          fileName: `⚠ ${file.name} (error al leer)`,
          mediaType: 'error',
          base64Data: '',
        });
        this.cdr.markForCheck();
      }
    };
    reader.readAsArrayBuffer(file);
  }

  /** Returns a short display label for an attachment (icon + filename). */
  attachmentLabel(att: ChatAttachment): string {
    if (att.mediaType === 'error') return att.fileName;
    if (att.mediaType.startsWith('image/')) return `📷 ${att.fileName}`;
    if (att.mediaType === 'application/pdf') return `📄 ${att.fileName}`;
    if (att.mediaType === 'text/csv') return `📊 ${att.fileName}`;
    if (att.mediaType === 'text/plain' || att.textContent) return `📄 ${att.fileName}`;
    return `📎 ${att.fileName}`;
  }

  /** Whether the attachment is an image (for thumbnail preview). */
  isImage(att: ChatAttachment): boolean {
    return att.mediaType.startsWith('image/') && att.base64Data.length > 0;
  }

  /** Data URI for image preview thumbnail. */
  imagePreviewUrl(att: ChatAttachment): string {
    return `data:${att.mediaType};base64,${att.base64Data}`;
  }
}
